import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function RegisterModal({ isOpen, onClose }: RegisterModalProps) {
  const [form, setForm] = useState({
    nombre: '',
    apellidos: '',
    usuario: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { openLoginModal } = useAuth();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!form.apellidos.trim()) newErrors.apellidos = 'Los apellidos son obligatorios';
    if (!form.usuario.trim()) newErrors.usuario = 'El usuario es obligatorio';
    else if (form.usuario.length < 4) newErrors.usuario = 'Mínimo 4 caracteres';
    else if (!/^[a-zA-Z0-9_]+$/.test(form.usuario)) newErrors.usuario = 'Solo letras, números y _';
    if (!form.email.trim()) newErrors.email = 'El email es obligatorio';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Email no válido';
    if (!form.password) newErrors.password = 'La contraseña es obligatoria';
    else if (form.password.length < 8) newErrors.password = 'Mínimo 8 caracteres';
    if (!form.confirmPassword) newErrors.confirmPassword = 'Confirma tu contraseña';
    else if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden';
    if (!acceptedTerms) newErrors.terms = 'Debes aceptar los términos para continuar';
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
    if (serverError) setServerError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setServerError('');

    try {
      // 1. Check username uniqueness
      const { data: existing } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', form.usuario)
        .maybeSingle();

      if (existing) {
        setErrors({ usuario: 'Este nombre de usuario ya está en uso' });
        setLoading(false);
        return;
      }

      // 2. Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          setErrors({ email: 'Este email ya está registrado' });
          setLoading(false);
          return;
        }

        // Handle email rate limit gracefully — account was created, just couldn't send the email
        const isRateLimit =
          authError.message.toLowerCase().includes('rate limit') ||
          authError.message.toLowerCase().includes('over_email_send_rate_limit') ||
          (authError as unknown as { code?: string }).code === 'over_email_send_rate_limit';

        if (!isRateLimit) {
          setServerError(authError.message);
          setLoading(false);
          return;
        }

        // Rate limit: account was created but email not sent — still show success
        if (!authData?.user) {
          setSubmitted(true);
          setLoading(false);
          return;
        }
        // Fall through to profile creation with the user that was returned
      }

      if (!authData?.user) {
        setServerError('Error al crear la cuenta. Inténtalo de nuevo.');
        setLoading(false);
        return;
      }

      // 3. Insert profile via edge function (bypasses RLS session requirement)
      const profileRes = await fetch(
        'https://obkzsvawsvwqrfsssuak.supabase.co/functions/v1/create-profile',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: authData.user.id,
            username: form.usuario,
            nombre: form.nombre,
            apellidos: form.apellidos,
            email: form.email,
          }),
        }
      );

      const profileData = await profileRes.json();

      if (!profileRes.ok || profileData.error) {
        setServerError(profileData.error ?? 'Error al guardar el perfil. Inténtalo de nuevo.');
        setLoading(false);
        return;
      }

      setSubmitted(true);
    } catch {
      setServerError('Error inesperado. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({ nombre: '', apellidos: '', usuario: '', email: '', password: '', confirmPassword: '' });
    setErrors({});
    setSubmitted(false);
    setServerError('');
    setAcceptedTerms(false);
    onClose();
  };

  const inputClass = (field: string) =>
    `w-full bg-[#1a1d26] border ${errors[field] ? 'border-red-500' : 'border-[#2a2d3a]'} rounded-lg px-4 py-3 text-white text-sm placeholder-[#4a5568] focus:outline-none focus:border-[#00e87a] transition-colors`;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-md bg-[#0d0f14] border border-[#2a2d3a] rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-7 pb-4 border-b border-[#2a2d3a]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-[#00e87a]/10 rounded-lg">
                <i className="ri-user-add-line text-[#00e87a] text-lg"></i>
              </div>
              <div>
                <h2 className="text-white font-bold text-lg leading-tight">Crear cuenta</h2>
                <p className="text-[#6b7280] text-xs">Únete a la comunidad de tipsters</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-[#00e87a]/10 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-[#00e87a] text-4xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">¡Registro completado!</h3>
              <p className="text-[#6b7280] text-sm mb-1">
                Bienvenido, <span className="text-[#00e87a] font-semibold">@{form.usuario}</span>
              </p>
              <p className="text-[#6b7280] text-xs mb-6">
                Hemos enviado un email de verificación a{' '}
                <span className="text-white">{form.email}</span>
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-[#00e87a] text-black text-sm font-bold rounded-lg hover:bg-[#00d070] transition-colors cursor-pointer whitespace-nowrap"
              >
                Empezar ahora
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Server error */}
              {serverError && (
                <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-red-400 text-sm"></i>
                  </div>
                  <p className="text-red-400 text-xs leading-relaxed">{serverError}</p>
                </div>
              )}

              {/* Nombre + Apellidos */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">Nombre</label>
                  <input
                    type="text"
                    name="nombre"
                    value={form.nombre}
                    onChange={handleChange}
                    placeholder="Carlos"
                    className={inputClass('nombre')}
                    autoComplete="given-name"
                  />
                  {errors.nombre && <p className="text-red-400 text-xs mt-1">{errors.nombre}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">Apellidos</label>
                  <input
                    type="text"
                    name="apellidos"
                    value={form.apellidos}
                    onChange={handleChange}
                    placeholder="García López"
                    className={inputClass('apellidos')}
                    autoComplete="family-name"
                  />
                  {errors.apellidos && <p className="text-red-400 text-xs mt-1">{errors.apellidos}</p>}
                </div>
              </div>

              {/* Usuario */}
              <div>
                <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">Nombre de usuario</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a5568] text-sm font-medium">@</span>
                  <input
                    type="text"
                    name="usuario"
                    value={form.usuario}
                    onChange={handleChange}
                    placeholder="carlosg_tips"
                    className={`${inputClass('usuario')} pl-7`}
                    autoComplete="username"
                  />
                </div>
                {errors.usuario && <p className="text-red-400 text-xs mt-1">{errors.usuario}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">Correo electrónico</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#4a5568]">
                    <i className="ri-mail-line text-sm"></i>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="carlos@gmail.com"
                    className={`${inputClass('email')} pl-9`}
                    autoComplete="email"
                  />
                </div>
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">Contraseña</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#4a5568]">
                    <i className="ri-lock-line text-sm"></i>
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Mínimo 8 caracteres"
                    className={`${inputClass('password')} pl-9`}
                    autoComplete="new-password"
                  />
                </div>
                {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs font-semibold text-[#a8b2c1] mb-1.5">Confirmar contraseña</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#4a5568]">
                    <i className="ri-lock-2-line text-sm"></i>
                  </div>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="Repite tu contraseña"
                    className={`${inputClass('confirmPassword')} pl-9`}
                    autoComplete="new-password"
                  />
                </div>
                {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>

              {/* Terms checkbox */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        if (errors.terms) setErrors({ ...errors, terms: '' });
                      }}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                        acceptedTerms
                          ? 'bg-[#00e87a] border-[#00e87a]'
                          : errors.terms
                          ? 'border-red-500 bg-transparent'
                          : 'border-[#2a2d3a] bg-transparent group-hover:border-[#00e87a]/50'
                      }`}
                    >
                      {acceptedTerms && <i className="ri-check-line text-black text-[10px] font-bold"></i>}
                    </div>
                  </div>
                  <span className="text-[#6b7280] text-xs leading-relaxed">
                    He leído y acepto los{' '}
                    <a
                      href={`${window.location.origin}/terminos`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00e87a] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Términos de uso
                    </a>{' '}
                    y la{' '}
                    <a
                      href={`${window.location.origin}/privacidad`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00e87a] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Política de privacidad
                    </a>
                    . Soy mayor de 18 años.
                  </span>
                </label>
                {errors.terms && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line text-xs"></i>
                    {errors.terms}
                  </p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#00e87a] text-black text-sm font-bold rounded-lg hover:bg-[#00d070] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Creando cuenta...
                  </>
                ) : (
                  <>
                    <i className="ri-user-add-line text-base"></i>
                    Crear cuenta gratis
                  </>
                )}
              </button>

              {/* Login link */}
              <p className="text-center text-xs text-[#6b7280]">
                ¿Ya tienes cuenta?{' '}
                <button
                  type="button"
                  onClick={() => { handleClose(); openLoginModal(); }}
                  className="text-[#00e87a] font-semibold hover:underline cursor-pointer"
                >
                  Inicia sesión
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
